import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';

import { API } from '../lib/api';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [token, setToken] = useState(() => localStorage.getItem("cuereq_token"));
  const session = useMemo(() => token ? { access_token: token } : null, [token]);

  // Helper: get token from localStorage
  const getToken = () => localStorage.getItem('cuereq_token');

  // Helper: set axios default auth header
  const setAuthHeader = (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // On mount, check for existing token and fetch user
  useEffect(() => {
    const token = getToken();
    if (token) {
      setAuthHeader(token);
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    setAuthError(''); setLoading(true);
    try {
      setAuthHeader(token);
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      if ([401, 403, 404].includes(err.response?.status)) {
        localStorage.removeItem('cuereq_token'); setToken(null); setAuthHeader(null); setUser(null);
      } else { setAuthError('Unable to connect to CueReq. Your sign-in is saved; please retry.'); }
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, name, role = 'user') => {
    const res = await axios.post(`${API}/auth/signup`, { email, password, name, role });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('cuereq_token', access_token);
    setToken(access_token);
    setAuthHeader(access_token);
    setUser(userData);
    return res.data;
  };

  const signIn = async (email, password) => {
    const res = await axios.post(`${API}/auth/signin`, { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('cuereq_token', access_token);
    setToken(access_token);
    setAuthHeader(access_token);
    setUser(userData);
    return res.data;
  };

  const saveIdentity = ({ access_token, user: identity }) => {
    localStorage.setItem('cuereq_token',access_token); setToken(access_token); setAuthHeader(access_token); setUser(identity); setAuthError('');
  };
  const joinGuest = async (sessionId,name) => {
    let deviceKey=localStorage.getItem('cuereq_guest_device');
    if (!deviceKey) { deviceKey=Array.from(crypto.getRandomValues(new Uint8Array(32)),byte=>byte.toString(16).padStart(2,'0')).join(''); localStorage.setItem('cuereq_guest_device',deviceKey); }
    const response=await axios.post(`${API}/auth/guest`,{ session_id:sessionId,name,device_key:deviceKey },{headers:{Authorization:undefined}});
    saveIdentity(response.data); return response.data;
  };
  const upgradeGuest = async (email,password) => {
    const response=await axios.post(`${API}/auth/upgrade-guest`,{email,password}); saveIdentity(response.data); return response.data;
  };

  const signOut = async () => {
    try {
      const token = getToken();
      if (token) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('cuereq_token');
      setToken(null);
      setAuthHeader(null);
      setUser(null);
      setAuthError('');
    }
  };

  const value = {
    user,
    loading,
    authError,
    token,
    session,
    signUp,
    signIn,
    signOut,
    joinGuest,
    upgradeGuest,
    refreshUser: () => fetchUser(getToken()),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
